import React, { useEffect, useMemo, useState } from "react";
import {
  FaBuilding,
  FaEnvelope,
  FaPaperPlane,
  FaTimes,
  FaUserCheck,
} from "react-icons/fa";
import { ClipLoader } from "react-spinners";

const expireOptions = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
];

const splitEmails = (value = "") =>
  String(value)
    .split(/[,\n;]/)
    .map((item) => item.trim())
    .filter(Boolean);

const SendToCustomerModal = ({
  isOpen,
  client,
  quotation,
  sending,
  onClose,
  onSubmit,
}) => {
  const approvers = useMemo(
    () =>
      Array.isArray(client?.authorizedApprovers)
        ? client.authorizedApprovers
        : [],
    [client?.authorizedApprovers]
  );
  const [selectedIndex, setSelectedIndex] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [ccText, setCcText] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(14);

  const selectedApprover = useMemo(() => {
    if (selectedIndex === "") return null;
    return approvers[Number(selectedIndex)] || null;
  }, [approvers, selectedIndex]);

  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(approvers.length > 0 ? "0" : "");
      setManualEmail("");
      setCcText("");
      setExpiresInDays(14);
    }
  }, [approvers.length, isOpen]);

  if (!isOpen) return null;

  const recipientEmail = selectedApprover?.email || manualEmail.trim();
  const ccEmails = splitEmails(ccText);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      to: recipientEmail,
      cc: ccEmails,
      expiresInDays: Number(expiresInDays),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6">
      <div className="w-full max-w-2xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <div>
            <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-300">
              <FaPaperPlane size={15} />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">
                Customer E-Sign
              </span>
            </div>
            <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
              ส่งใบเสนอราคาให้ลูกค้าลงนาม
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              เลือกผู้รับอีเมลและกำหนดวันหมดอายุของ secure signing link
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="rounded-md p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-5 py-5">
          <section className="grid gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-800 dark:bg-gray-950/60 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <div className="mb-1 flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-100">
                <FaBuilding className="text-indigo-500" />
                Client Information
              </div>
              <p className="text-gray-900 dark:text-gray-100">
                {client?.customerName || quotation?.client || "-"}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {client?.companyBaseName || "-"}
              </p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                Contact Email
              </span>
              <p className="mt-1 text-gray-800 dark:text-gray-200">
                {client?.email || "-"}
              </p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                Tax ID
              </span>
              <p className="mt-1 text-gray-800 dark:text-gray-200">
                {client?.taxIdentificationNumber || "-"}
              </p>
            </div>
          </section>

          <div>
            <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
              <FaUserCheck className="text-gray-400" />
              ผู้รับอีเมลสำหรับลงนาม
            </label>
            {approvers.length > 0 ? (
              <select
                value={selectedIndex}
                onChange={(event) => setSelectedIndex(event.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:focus:ring-indigo-950"
                required
              >
                {approvers.map((approver, index) => (
                  <option key={approver._id || `${approver.email}-${index}`} value={index}>
                    {approver.name || "Unnamed"} - {approver.email || "No email"}
                    {approver.position ? ` (${approver.position})` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="email"
                value={manualEmail}
                onChange={(event) => setManualEmail(event.target.value)}
                placeholder="customer@example.com"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:focus:ring-indigo-950"
                required
              />
            )}
            {selectedApprover && (
              <div className="mt-2 rounded-md bg-indigo-50 px-3 py-2 text-xs text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200">
                ส่งถึง {selectedApprover.name || "-"}{" "}
                {selectedApprover.position ? `ตำแหน่ง ${selectedApprover.position}` : ""}
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
            <label className="block">
              <span className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                <FaEnvelope className="text-gray-400" />
                CC
              </span>
              <input
                value={ccText}
                onChange={(event) => setCcText(event.target.value)}
                placeholder="name@example.com, other@example.com"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:focus:ring-indigo-950"
              />
              <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                ใส่หลายอีเมลได้ โดยคั่นด้วย comma
              </span>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Link expires
              </span>
              <select
                value={expiresInDays}
                onChange={(event) => setExpiresInDays(event.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:focus:ring-indigo-950"
              >
                {expireOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {ccEmails.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {ccEmails.map((cc) => (
                <span
                  key={cc}
                  className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200"
                >
                  {cc}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 dark:border-gray-800 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="rounded-md border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={sending || !recipientEmail}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {sending ? <ClipLoader color="#fff" size={16} /> : <FaPaperPlane />}
              ส่งอีเมลให้ลูกค้า
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendToCustomerModal;
